import {
	ChangeDetectionStrategy,
	Component,
	Input,
	ViewChild,
	AfterViewInit,
} from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import {
	BrokerageType,
	CommissionSettings,
	CommissionType,
	DepositHolder,
	MatterAdjustmentDepositRecordCalculated,
	DepositHolderSelections,
	ExcessDepositRecipient,
	ExcessDepositRecipientOptions,
	MatterFileType,
} from '@models/matter';
import { Select, Store } from '@ngxs/store';
import { CommissionCalculated } from '@services/financialTotals/financial-totals.service';
import { CommissionSelectors } from '@state/global/commission';
import { MatterDispatch } from '@state/matter';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	DefaultDepositHolderValue,
	DepositDrawerComponent,
} from '../../fields/deposit/deposit-drawer/deposit-drawer.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DocumentProcessorService } from '@services/document-ai/document-processor.service';
import { MatterDocumentType } from '@models/matter-file';
import { TestHooks } from './commission-details.hooks';
import { deleteField } from '@angular/fire/firestore';
import { NavigationService } from '@services/nav/nav.service';

export enum COMMISSION_DETAILS_FRAGMENT {
	DETAILS = 'details',
}

@Component({
	selector: 'app-commission-details',
	templateUrl: './commission-details.component.html',
	animations: [fade(), openClose(300)],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CommissionDetailsComponent implements AfterViewInit {
	testHooks = TestHooks;
	COMMISSION_DETAILS_FRAGMENT = COMMISSION_DETAILS_FRAGMENT;
	public ICON = ICON;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_ICON_CLASSES = AI_GENERATED_ICON.classes;
	public aiScrapingLoading$: Observable<boolean> = EMPTY;
	public commissionType = CommissionType;
	public commissionTypeOptions = [
		{ label: 'Percentage', value: CommissionType.PERCENTAGE },
		{ label: 'Flat fee', value: CommissionType.FLAT_FEE },
	];
	public matterDocumentType: MatterDocumentType;
	private get depositHolder$() {
		return this.store.select(SelectedMatter.getSelectedDepositHolder);
	}
	public commissionSettings$: Observable<CommissionSettings> = EMPTY;
	public commissionCalculated$: Observable<CommissionCalculated>;
	public intercomAttrValue: string;
	private commissionSettingsDirection:
		| 'vendorCommissionSettings'
		| 'purchaserCommissionSettings' = 'vendorCommissionSettings';
	public aiCommissionsTotalTooltip$: Observable<string> = of('');
	public aiCommissionsTypeTooltip$: Observable<string> = of('');
	public aiCommissionsValueTooltip$: Observable<string> = of('');
	public aiCommissionsApplyHSTTooltip$: Observable<string> = of('');

	@Input() set selectedBrokerageType(brokerageType: BrokerageType) {
		this.intercomAttrValue =
			brokerageType === BrokerageType.BUYER_BROKERAGE &&
			this.store.selectSnapshot(SelectedMatter.matterFileType) ===
				MatterFileType.SALE
				? 'buyer-brokerage-commission-balance'
				: null;

		switch (brokerageType) {
			case BrokerageType.SELLER_BROKERAGE: {
				this.matterDocumentType =
					MatterDocumentType.SELLER_COMMISSION_STATEMENT;
				this.commissionSettingsDirection = 'vendorCommissionSettings';
				this.commissionSettings$ = this.store.select(
					SelectedMatter.vendorCommissionSettings,
				);
				this.commissionCalculated$ = this.store.select(
					CommissionSelectors.vendorCommissionCalculated,
				);
				this.selectedBrokerageIsDepositHolder$ = this.depositHolder$.pipe(
					map(
						(depositHolder) => depositHolder === DepositHolder.VENDOR_BROKERAGE,
					),
				);
				break;
			}
			case BrokerageType.BUYER_BROKERAGE: {
				this.commissionSettingsDirection = 'purchaserCommissionSettings';
				this.matterDocumentType = MatterDocumentType.BUYER_COMMISSION_STATEMENT;
				this.commissionSettings$ = this.store.select(
					SelectedMatter.purchaserCommissionSettings,
				);
				this.commissionCalculated$ = this.store.select(
					CommissionSelectors.purchaserCommissionCalculated,
				);
				this.selectedBrokerageIsDepositHolder$ = this.depositHolder$.pipe(
					map(
						(depositHolder) =>
							depositHolder === DepositHolder.PURCHASE_BROKERAGE,
					),
				);
				break;
			}
		}

		this.aiScrapingLoading$ = this.documentProcessorService.isLoading$(
			this.matterDocumentType,
		);
		const aiCommissionsFieldMappings$ = this.store
			.select(SelectedMatter.getFieldMappings)
			.pipe(
				map(
					(fieldMappings) => fieldMappings?.[this.commissionSettingsDirection],
				),
			);

		this.aiCommissionsTotalTooltip$ = aiCommissionsFieldMappings$.pipe(
			map((commissionKeys) =>
				Object.values(commissionKeys ?? {}).length ||
				this.store.selectSnapshot(SelectedMatter.getFieldMappings)
					?.purchasePrice
					? AI_GENERATED_ICON.tooltip
					: '',
			),
		);

		this.aiCommissionsTypeTooltip$ = aiCommissionsFieldMappings$.pipe(
			map((commissionKeys) =>
				commissionKeys?.type ? AI_GENERATED_ICON.tooltip : '',
			),
		);

		this.aiCommissionsValueTooltip$ = aiCommissionsFieldMappings$.pipe(
			map((commissionKeys) =>
				commissionKeys?.value ? AI_GENERATED_ICON.tooltip : '',
			),
		);

		this.aiCommissionsApplyHSTTooltip$ = aiCommissionsFieldMappings$.pipe(
			map((commissionKeys) =>
				commissionKeys?.applyHST ? AI_GENERATED_ICON.tooltip : '',
			),
		);
	}
	@Input() brokerageTypeDisplayName: string;
	public selectedBrokerageIsDepositHolder$: Observable<boolean> = EMPTY;
	public excessDepositRecipientOptions = ExcessDepositRecipientOptions;
	public showDepositEditControl$ = new BehaviorSubject(false);
	@ViewChild('depositDrawer') depositDrawer: DepositDrawerComponent;
	@Select(SelectedMatter.matterHST) hstPercentage$: Observable<number>;
	@Select(AdjustmentsSelector.depositAdjustmentCalculated)
	depositTotal$: Observable<MatterAdjustmentDepositRecordCalculated>;
	public depositHolderDisplayName$ = this.depositHolder$.pipe(
		map(
			(depositHolder) =>
				DepositHolderSelections.find((x) => x.value === depositHolder)?.name ??
				'',
		),
	);

	constructor(
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
		private documentProcessorService: DocumentProcessorService,
		private store: Store,
		private navService: NavigationService,
	) {}

	ngAfterViewInit(): void {
		this.navService.scrollToElement([
			'shadow-xl',
			'shadow-grey-600',
			'rounded-xl',
			'animation',
			'ease-in-out',
		]);
	}

	private updateTypeAndValue(
		type: CommissionType,
		value: number,
		fieldMappingKeyToDelete: 'type' | 'value' = 'value',
	) {
		this.matterDispatch.OverwriteFields(
			`${this.commissionSettingsDirection}.type`,
			type,
			[`${this.commissionSettingsDirection}.value`, value],
			[
				`fieldMappings.${this.commissionSettingsDirection}.${fieldMappingKeyToDelete}`,
				deleteField(),
			],
		);
	}

	onCommTypeChange(newCommType: CommissionType) {
		this.commissionCalculated$.pipe(take(1)).subscribe((currCommCalculated) => {
			const newVal = {
				[CommissionType.FLAT_FEE]: currCommCalculated.payable,
				[CommissionType.PERCENTAGE]: currCommCalculated.percentage,
			}[newCommType];
			this.updateTypeAndValue(newCommType, newVal, 'type');
		});
	}

	onPercentChange(percentage: string) {
		this.updateTypeAndValue(CommissionType.PERCENTAGE, parseFloat(percentage));
	}

	onCommissionPayableChange(amount: number) {
		this.updateTypeAndValue(CommissionType.FLAT_FEE, amount);
	}

	onApplyHstChange(applyHST: boolean) {
		this.matterDispatch.OverwriteFields(
			`${this.commissionSettingsDirection}.applyHST`,
			applyHST,
		);
	}

	onApplyDepositToCommissionChange(applyDeposit: boolean) {
		this.matterDispatch.OverwriteFields(
			`${this.commissionSettingsDirection}.applyDeposit`,
			applyDeposit,
		);
	}

	onExcessDepositRecipientChange(
		excessDepositRecipient: ExcessDepositRecipient,
	) {
		this.matterDispatch.OverwriteFields(
			`${this.commissionSettingsDirection}.excessDepositRecipient`,
			excessDepositRecipient,
		);
	}

	openDepositDrawer() {
		this.depositDrawer.openDrawer();
	}

	showHideDepositEditControl(showEditControl: boolean) {
		this.showDepositEditControl$.next(showEditControl);
	}

	onDepositHolderSelectionChange(depositHolder: DepositHolder) {
		// Track deposit holder selections
		if (depositHolder !== DefaultDepositHolderValue) {
			this.analytics.logEvent(
				AnalyticsEvent.COMMISSIONS_DEPOSIT_HOLDER_DROPDOWN_SELECTION_CHANGE,
			);
		}
	}
}
